import { initAllEdenProductCards } from './eden-card.js?v=20260829-cardfix';

function buildOuterRingPath(width, height, inset, cornerRadius) {
  const innerW = width - inset * 2;
  const innerH = height - inset * 2;
  const r = Math.min(cornerRadius, innerW / 2, innerH / 2);
  const x = inset;
  const y = inset;
  const right = x + innerW;
  const bottom = y + innerH;

  return [
    `M ${x + r} ${y}`,
    `L ${right - r} ${y}`,
    `A ${r} ${r} 0 0 1 ${right} ${y + r}`,
    `L ${right} ${bottom - r}`,
    `A ${r} ${r} 0 0 1 ${right - r} ${bottom}`,
    `L ${x + r} ${bottom}`,
    `A ${r} ${r} 0 0 1 ${x} ${bottom - r}`,
    `L ${x} ${y + r}`,
    `A ${r} ${r} 0 0 1 ${x + r} ${y}`,
    'Z',
  ].join(' ');
}

function syncCollectionHeroRing(frame) {
  const svg = frame?.querySelector('.eden-collection-hero__progress-svg');
  const track = frame?.querySelector('[data-eden-media-track]');
  const ring = frame?.querySelector('[data-eden-media-progress]');
  if (!svg || !track || !ring) return;

  const styles = getComputedStyle(frame);
  const ringWidth = parseFloat(styles.getPropertyValue('--eden-ring-width')) || 3;
  const ringPadding = parseFloat(styles.getPropertyValue('--eden-ring-padding')) || 15;
  const radius = parseFloat(styles.getPropertyValue('--eden-ring-radius')) || 24;

  const frameRect = frame.getBoundingClientRect();
  const width = Math.round(frameRect.width);
  const height = Math.round(frameRect.height);
  if (!width || !height) return;

  const inset = ringWidth / 2;
  const cornerRadius = radius + ringPadding - ringWidth / 2;
  const path = buildOuterRingPath(width, height, inset, cornerRadius);

  svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
  track.setAttribute('d', path);
  ring.setAttribute('d', path);
}

function initCollectionHeroMedia(wrap) {
  if (wrap.dataset.edenHeroReady === '1') return;
  wrap.dataset.edenHeroReady = '1';

  const frame = wrap.closest('.eden-collection-hero__media-frame');
  const imageLayer = wrap.querySelector('[data-eden-media-image]');
  const videoLayer = wrap.querySelector('[data-eden-media-video]');
  const video = videoLayer?.querySelector('video');
  const progressRing = frame?.querySelector('[data-eden-media-progress]');

  if (!imageLayer || !videoLayer || !video) return;

  const intervalSec = Number(wrap.dataset.edenMediaInterval) || 8;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let showingVideo = false;
  let videoBroken = false;
  let cycleTimer = null;

  const syncRing = () => syncCollectionHeroRing(frame);

  function canPlayVideo() {
    return !videoBroken && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA;
  }

  function showImage() {
    showingVideo = false;
    imageLayer.classList.add('is-active');
    videoLayer.classList.remove('is-active');
    video.pause();
    video.currentTime = 0;
  }

  function showVideo() {
    if (!canPlayVideo()) {
      showImage();
      return;
    }

    showingVideo = true;
    imageLayer.classList.remove('is-active');
    videoLayer.classList.add('is-active');
    const playPromise = video.play();
    if (playPromise?.catch) {
      playPromise.catch(() => {
        videoBroken = true;
        showImage();
      });
    }
  }

  function toggleMedia() {
    if (showingVideo) showImage();
    else showVideo();
  }

  function restartProgress() {
    if (!progressRing || reducedMotion) return;

    syncRing();
    progressRing.classList.remove('is-running');
    void progressRing.getBoundingClientRect();
    progressRing.classList.add('is-running');
  }

  function stopCycle() {
    if (cycleTimer) {
      clearInterval(cycleTimer);
      cycleTimer = null;
    }
  }

  function startCycle() {
    if (frame) frame.style.setProperty('--eden-media-interval', `${intervalSec}s`);
    stopCycle();

    if (reducedMotion || !progressRing) {
      cycleTimer = setInterval(toggleMedia, intervalSec * 1000);
      return;
    }

    restartProgress();
    cycleTimer = setInterval(() => {
      toggleMedia();
      restartProgress();
    }, intervalSec * 1000);
  }

  function beginWithVideo() {
    if (canPlayVideo()) {
      showVideo();
      startCycle();
      return;
    }

    showImage();
    const onReady = () => {
      if (videoBroken) return;
      showVideo();
      if (!cycleTimer) startCycle();
    };

    video.addEventListener('loadeddata', onReady, { once: true });
    video.addEventListener('canplay', onReady, { once: true });
    video.load();
    startCycle();
  }

  video.addEventListener('error', () => {
    videoBroken = true;
    showImage();
  });

  syncRing();
  beginWithVideo();

  window.addEventListener('resize', syncRing, { passive: true });

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      frame?.classList.add('is-paused');
      video.pause();
      stopCycle();
    } else {
      frame?.classList.remove('is-paused');
      syncRing();
      if (showingVideo && !videoBroken) video.play().catch(() => {});
      startCycle();
    }
  });
}

function initCollectionSearch(catalog) {
  const input = catalog.querySelector('[data-eden-collection-search]');
  const cards = catalog.querySelectorAll('[data-eden-product-card]');
  const empty = catalog.querySelector('[data-eden-collection-empty]');
  const countEl = catalog.querySelector('[data-eden-collection-count]');

  if (!input || !cards.length) return;

  const total = cards.length;

  input.addEventListener('input', () => {
    const query = input.value.trim().toLocaleLowerCase('pt-BR');
    let visible = 0;

    cards.forEach((card) => {
      const text = card.textContent.toLocaleLowerCase('pt-BR');
      const show = !query || text.includes(query);
      card.classList.toggle('is-hidden', !show);
      if (show) visible += 1;
    });

    if (empty) empty.hidden = visible > 0;
    if (countEl) {
      countEl.textContent = query
        ? `${visible} de ${total} produto${total === 1 ? '' : 's'} encontrado${visible === 1 ? '' : 's'}`
        : `${total} produto${total === 1 ? '' : 's'} nesta categoria`;
    }
  });
}

document.querySelectorAll('[data-eden-collection-media]').forEach(initCollectionHeroMedia);

const catalog = document.querySelector('[data-eden-collection-catalog]');
if (catalog && catalog.dataset.edenCatalogReady !== '1') {
  catalog.dataset.edenCatalogReady = '1';
  initCollectionSearch(catalog);
  initAllEdenProductCards(catalog);
}
